import Glide, { Breakpoints, Autoplay } from '@glidejs/glide/dist/glide.modular.esm'

const defaultParams = {
  type: 'slider',
  bound: true,
  focusAt: 0,
  startAt: 0,
  autoplay: false,
  perView: 5,
  breakpoints: {
    768: { perView: 1 },
    1200: { perView: 3 },
  },
  rewind: true,
}

const singleImageParams = {
  type: 'carousel',
  focusAt: 'center',
  startAt: 0,
  perView: 1,
  autoplay: false,
  gap: 24,
  peek: { before: 150, after: 150 },
}

// A full-width, one-slide-at-a-time slider (no peek, clean cut). Unlike
// defaultParams, perView stays 1 at every breakpoint so it can still page
// through a small number of slides (defaultParams' bound pagination goes
// dead when the slide count doesn't exceed perView, e.g. 3 slides at perView 5).
const standardSliderParams = {
  type: 'slider',
  bound: true,
  focusAt: 0,
  startAt: 0,
  autoplay: false,
  perView: 1,
  gap: 0,
  rewind: true,
}

// Glide sets each slide's pixel width from `Sizes.width - peek`, then reuses that
// same pixel value to compute the carousel's translate offset. A CSS width
// override on the slides desyncs that translate math (worst on narrow
// viewports, where the fixed `peek` is a large share of the container width).
// `data-peek-percent` keeps peek proportional to the container instead, so
// Glide's own computed width matches what's rendered and the offsets stay in sync.
const computePercentPeek = (glideElement, percent) => {
  const reduction = Math.round(glideElement.offsetWidth * (percent / 100))
  return { before: reduction, after: reduction }
}

const setParams = (glideElement) => {
  let baseParams = defaultParams
  if (glideElement.classList.contains('single-image')) {
    baseParams = singleImageParams
  } else if (glideElement.classList.contains('standard-slider')) {
    baseParams = standardSliderParams
  }
  const autoplayAttr = glideElement.getAttribute('data-autoplay')
  let autoplayValue = baseParams.autoplay

  if (autoplayAttr) {
    autoplayValue = parseInt(autoplayAttr, 10)
  }

  const peekPercentAttr = glideElement.getAttribute('data-peek-percent')
  const peekValue = peekPercentAttr
    ? computePercentPeek(glideElement, parseFloat(peekPercentAttr))
    : baseParams.peek || { before: 0, after: 0 }

  return {
    ...baseParams,
    autoplay: autoplayValue,
    peek: peekValue
  }
}

document.addEventListener('turbo:load', () => {
  document.querySelectorAll('.glide').forEach((glideElement) => {
    const glide = new Glide(glideElement, setParams(glideElement))
    const peekPercentAttr = glideElement.getAttribute('data-peek-percent')

    const updateArrows = () => {
      const nextButton = glideElement.querySelector('.glide__arrow--right')
      const prevButton = glideElement.querySelector('.glide__arrow--left')

      if (glide.settings.type === 'carousel') {
        if (prevButton) {
          prevButton.disabled = false
          prevButton.classList.remove('disabled')
        }

        if (nextButton) {
          nextButton.disabled = false
          nextButton.classList.remove('disabled')
        }

        return
      }

      const isFirstSlide = glide.index === 0
      const isLastSlide = glide.index === glideElement.querySelectorAll('.glide__slide').length - glide._o.perView

      if (prevButton) {
        prevButton.disabled = isFirstSlide
        prevButton.classList.toggle('disabled', isFirstSlide)
      }

      if (nextButton) {
        nextButton.disabled = isLastSlide
        nextButton.classList.toggle('disabled', isLastSlide)
      }
    }

    glide.on('run', updateArrows)
    glide.mount({ Breakpoints, Autoplay })
    updateArrows()

    if (peekPercentAttr) {
      let resizeTimeout

      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout)
        resizeTimeout = setTimeout(() => {
          glide.update({ peek: computePercentPeek(glideElement, parseFloat(peekPercentAttr)) })
        }, 150)
      })
    }

    glideElement.querySelectorAll('.glide__arrow--left').forEach((arrow) => {
      arrow.addEventListener('click', () => glide.go('<'))
    })
    glideElement.querySelectorAll('.glide__arrow--right').forEach((arrow) => {
      arrow.addEventListener('click', () => glide.go('>'))
    })
  })
})
